import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const SponsoredProducts = () => {
  const selectedType = useSelector((state) => state.typeFilter);
  const [sponsored, setSponsored] = useState([]);
  const [authors, setAuthors] = useState({});
  const [failedImages, setFailedImages] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const containerRef = useRef(null);
  const autoPlayRef = useRef(null);
  const API = process.env.REACT_APP_API;

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to determine product type based on category
  const getProductType = (product) => {
    const categoryName = product?.category?.name || '';
    if (categoryName.toLowerCase().includes('imóvel')) {
      return 'imovel';
    }
    if (categoryName.toLowerCase().includes('serviço')) {
      return 'servico';
    }
    return 'produto';
  };

  const loadSponsored = async () => {
    try {
      const res = await axios.get(`${API}/admin/sponsored-ads`);
      // Mapeia 'imovel' para 'produto'
      const expectedType = selectedType === 'imovel' ? 'produto' : selectedType;
      setSponsored(res.data.filter((ad) => ad.active && ad.product && ad.product.type === expectedType));
      console.log('Sponsored ads loaded:', res.data);
    } catch (error) {
      console.error('Erro ao carregar anúncios patrocinados:', error);
    }
  };

  useEffect(() => {
    loadSponsored();
    // eslint-disable-next-line
  }, [selectedType]);

  // Fetch authors
  useEffect(() => {
    const fetchAuthors = async () => {
      const missing = sponsored
        .map(ad => ad.product?.author)
        .filter(author => author && typeof author === 'string' && !authors[author]);
      const uniqueMissing = [...new Set(missing)];
      if (uniqueMissing.length === 0) return;
      const newAuthors = { ...authors };
      await Promise.all(
        uniqueMissing.map(async (id) => {
          try {
            const res = await axios.get(`${API}/user/${id}`);
            newAuthors[id] = res.data;
          } catch (e) {
            newAuthors[id] = { name: 'Vendedor' };
          }
        })
      );
      setAuthors(newAuthors);
    };
    if (sponsored.length) fetchAuthors();
    // eslint-disable-next-line
  }, [sponsored]);

  const sortedSponsored = sponsored.sort((a, b) => (a.order || 0) - (b.order || 0));
  const totalSlides = sortedSponsored.length;

  const goToSlide = useCallback((index) => {
    if (totalSlides === 0) return;
    let newIndex = index;
    if (index < 0) newIndex = totalSlides - 1;
    if (index >= totalSlides) newIndex = 0;
    setCurrentIndex(newIndex);
  }, [totalSlides]);

  const nextSlide = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  // Auto-play
  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, nextSlide, totalSlides]);

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  if (!sponsored.length) {
    console.log('No sponsored ads to render');
    return null;
  }

  const currentAd = sortedSponsored[currentIndex];
  const author = typeof currentAd?.product?.author === 'string' 
    ? authors[currentAd.product.author] 
    : currentAd?.product?.author;

  const getImageUrl = (ad) => {
    if (!ad?.product?.images?.length) return null;
    const img = ad.product.images[0];
    return img.startsWith('http') ? img : `${API.replace('/api', '')}${img}`;
  };

  // Get 3 visible slides for the carousel effect
  const getVisibleSlides = () => {
    if (totalSlides === 0) return [];
    if (totalSlides === 1) return [{ ad: sortedSponsored[0], position: 'center' }];
    if (totalSlides === 2) {
      return [
        { ad: sortedSponsored[currentIndex], position: 'center' },
        { ad: sortedSponsored[(currentIndex + 1) % totalSlides], position: 'right' }
      ];
    }
    
    const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides;
    const nextIndex = (currentIndex + 1) % totalSlides;
    
    return [
      { ad: sortedSponsored[prevIndex], position: 'left' },
      { ad: sortedSponsored[currentIndex], position: 'center' },
      { ad: sortedSponsored[nextIndex], position: 'right' }
    ];
  };

  return (
    <section 
      className="sponsored-hero"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background with parallax effect */}
      <div className="sponsored-hero-bg">
        {sortedSponsored.map((ad, idx) => {
          const imgUrl = getImageUrl(ad);
          return (
            <div 
              key={ad._id}
              className={`sponsored-hero-bg-image ${idx === currentIndex ? 'active' : ''}`}
              style={{ backgroundImage: imgUrl ? `url(${imgUrl})` : 'none' }}
            />
          );
        })}
        <div className="sponsored-hero-overlay" />
      </div>

      {/* Main content */}
      <div className="sponsored-hero-content">
        {/* Carousel Container */}
        <div 
          className="sponsored-carousel-container"
          ref={containerRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation Arrows */}
          {totalSlides > 1 && (
            <>
              <button 
                className="sponsored-nav-btn sponsored-nav-prev"
                onClick={prevSlide}
                aria-label="Anterior"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button 
                className="sponsored-nav-btn sponsored-nav-next"
                onClick={nextSlide}
                aria-label="Próximo"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </>
          )}

          {/* Cards Carousel */}
          <div className="sponsored-carousel-track">
            {getVisibleSlides().map(({ ad, position }) => {
              const imgUrl = getImageUrl(ad);
              const cardAuthor = typeof ad.product?.author === 'string' 
                ? authors[ad.product.author] 
                : ad.product?.author;
              
              return (
                <Link
                  key={`${ad._id}-${position}`}
                  to={`/product/${ad.product?._id}`}
                  className={`sponsored-carousel-card ${position}`}
                  onClick={(e) => {
                    if (position !== 'center') {
                      e.preventDefault();
                      if (position === 'left') prevSlide();
                      if (position === 'right') nextSlide();
                    }
                  }}
                >
                  <div className="sponsored-card-inner">
                    {/* Image */}
                    <div className="sponsored-card-image-wrapper">
                      {imgUrl && !failedImages.has(imgUrl) ? (
                        <img 
                          src={imgUrl}
                          alt={ad.product?.name}
                          className="sponsored-card-img"
                          onError={() => setFailedImages(prev => new Set(prev).add(imgUrl))}
                        />
                      ) : (
                        <div className="sponsored-card-placeholder">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                        </div>
                      )}
                      <div className="sponsored-card-gradient" />
                    </div>

                    {/* Card Info Overlay */}
                    <div className="sponsored-card-info">
                      <h3 className="sponsored-card-title">{ad.product?.name}</h3>
                      <div className="sponsored-card-meta">
                        <span className="sponsored-card-seller">
                          {cardAuthor?.name || 'Vendedor'}
                        </span>
                        {ad.product?.price && (
                          <span className="sponsored-card-price">
                            R$ {Number(ad.product.price).toLocaleString('pt-BR')}
                          </span>
                        )}
                      </div>
                      <div className="sponsored-card-cta">
                        <span>Ver detalhes</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SponsoredProducts;
